import { useState, useEffect } from 'react'
import { Dialog, IconButton, Typography, Tab } from '@mui/material'
import { getRequest } from 'utils/api'
import { ASSESSMENT_RATINGS_TIMELINE_URL } from 'services/constants'
import { Close } from '@mui/icons-material'
import RowStack from 'components/atoms/container/RowStack'
import { ColStack } from 'components/atoms/container/InCardStack'
import { Tag } from 'components/atoms/Text'
import { formatDate } from 'utils/formatDateTime'
import { IconContainer } from 'components/atoms/Icons'
import BackIconButton from 'components/atoms/button/BackIconButton'
import NextIconButton from 'components/atoms/button/NextIconButton'
import ToggleTabs from 'components/atoms/button/ToggleTabs'
import BehaviourDetails from 'components/molecules/assessments/pathAssessment/BehaviourDetails'
import BehaviourComparison from 'components/molecules/assessments/pathAssessment/BehaviourComparison'
import RequestErrorPageCard from 'components/molecules/RequestErrorPageCard'
import { useTranslation } from 'react-i18next'

const AssessmentBehaviourPopup = ({
  open,
  onClose,
  active = 0,
  assessmentData,
  prevAssessmentData = null,
  behaviour
}) => {
  const { t, i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)
  const [behaviourIndex, setBehaviourIndex] = useState(null)
  const [behaviourData, setBehaviourData] = useState(null)
  const [prevAssessment, setPrevAssessment] = useState(null)
  const [requestError, setRequestError] = useState(undefined)

  useEffect(() => {
    if (behaviourIndex >= 0) {
      setBehaviourData(assessmentData?.behaviourInfoData[behaviourIndex])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [behaviourIndex])

  useEffect(() => {
    if (open && behaviour) {
      setActiveTab(active)

      // Find the index of the behaviour in the assessment data
      const behaviourIndex = assessmentData?.behaviourInfoData?.findIndex(
        i => i.name.trim() === behaviour.trim()
      )
      setBehaviourIndex(behaviourIndex)

      // Fetch the previous assessment data if not already provided
      const fetchData = async () => {
        const res = await getRequest(ASSESSMENT_RATINGS_TIMELINE_URL, {
          goalId: assessmentData?.goalData?.id
        })
        if (res.status === 200) {
          const assessments = res.data.data
          if (assessments.length > 1) {
            setPrevAssessment(assessments[1])
          }
          setRequestError(false)
        } else {
          setRequestError(true)
        }
      }
      if (!prevAssessmentData) {
        fetchData()
      } else {
        setPrevAssessment(prevAssessmentData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const navBtnSx = {
    minWidth: 40,
    height: 40,
    borderRadius: 25,
    padding: 0,
    '& .MuiSvgIcon-root': {
      fontSize: 20
    }
  }

  return (
    <Dialog onClose={() => onClose()} open={open} maxWidth='lg'>
      <ColStack
        spacing={8}
        sx={{ width: 720, height: 820, padding: 12, overflowY: 'auto' }}>
        <RowStack justifyContent='space-between'>
          <Tag sx={{ padding: 2, lineHeight: '120%' }}>
            {formatDate(assessmentData?.ratingTimeStamp, i18n.resolvedLanguage)}
          </Tag>
          <IconButton onClick={() => onClose()}>
            <IconContainer icon={<Close />} size={24} />
          </IconButton>
        </RowStack>

        <RowStack justifyContent='space-between'>
          <Typography variant='h2'>{behaviourData?.name}</Typography>

          <RowStack spacing={4} width='auto'>
            <BackIconButton
              sx={navBtnSx}
              disabled={behaviourIndex === 0}
              onClick={() => {
                setBehaviourIndex(behaviourIndex - 1)
              }}
            />
            <NextIconButton
              sx={navBtnSx}
              disabled={
                behaviourIndex === assessmentData?.behaviourInfoData?.length - 1
              }
              onClick={() => {
                setBehaviourIndex(behaviourIndex + 1)
              }}
            />
          </RowStack>
        </RowStack>

        {!requestError ? (
          <>
            <ToggleTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}>
              <Tab value={0} label={t('assessmentTimeline.latest360')} />
              <Tab value={1} label={t('assessmentTimeline.timeComparison')} />
            </ToggleTabs>

            {activeTab === 0 && (
              <BehaviourDetails behaviourData={behaviourData} />
            )}
            {activeTab === 1 && (
              <BehaviourComparison
                currentAssessment={assessmentData}
                prevAssessment={prevAssessment}
                behaviour={behaviourData?.name}
              />
            )}
          </>
        ) : (
          <RequestErrorPageCard />
        )}
      </ColStack>
    </Dialog>
  )
}

export default AssessmentBehaviourPopup
